import { AssignMemberListModal } from 'components';
import { useAuthContext } from 'contexts';
import type { Communication, Patient } from 'fhir/r4';
import { FHIRProxyClient, ProvenanceBuilder } from 'fhir-sdoh';
import moment from 'moment';
import React, { useState } from 'react';
import { Button, Confirm, Icon, Popup, Table } from 'semantic-ui-react';
import type { SemanticCOLORS, SemanticICONS } from 'semantic-ui-react';
//import * as Connect from '../../../../../services/connect';
import { fhirPut } from 'services';
import type { CommunicationData } from 'types';
import { CommunicationHelper, ErrorResponse } from 'utils';

type ZendeskRowProps = {
  debugCommunication: Communication;
  communicationsMutate: () => Promise<CommunicationData | undefined>;
  handleErrorMessage: (errorMessage: string) => void;
};

const ZendeskRow: React.FC<ZendeskRowProps> = ({
  debugCommunication,
  communicationsMutate,
  handleErrorMessage,

}) => {
  const { practitionerId, practitionerName, token } = useAuthContext();

  // Seeded `undefined` but reset with `null` by the delete handler and the confirm
  // dialog's cancel, so the state really holds both spellings of absent.
  const [deleteCall, setDeleteCall] = useState<Communication | null | undefined>(undefined);
  const [isAssignMemberModalOpen, setIsAssignMemberModalOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // Reset with `null` on close, so the state genuinely holds all three.
  const [selectedCallerCard, setSelectedCallerCard] = useState<Patient | null | undefined>(undefined);
  const [selectedCallTicket, setSelectedCallTicket] = useState<CommunicationHelper | null>(null);

  const handleAssignClick = (communicationData: CommunicationHelper) => {
    setSelectedCallTicket(communicationData);
    setIsAssignMemberModalOpen(true);
  };

  const handleAssignMember = async (communicationData: CommunicationHelper) => {
    setIsSubmitting(true);

    const fhirProxyClient = new FHIRProxyClient(token, `${import.meta.env.VITE_API_ENDPOINT}/fhir/`);

    const actorReference = { reference: `Practitioner/${practitionerId}`, display: practitionerName };
    // `managingOrganization` and its `reference` are both optional and read
    // unguarded here, as is `selectedCallerCard` itself.
    const orgId = selectedCallerCard!.managingOrganization!.reference!.split('/')[1];
    const organizationReference = { code: orgId?.toUpperCase(), display: orgId?.toLowerCase() };
    // `Patient.id` is `0..1`; the existing `selectedCallerCard!` already assumes the
    // card is present.
    const patientId = selectedCallerCard!.id!;
    const inboundPayload = communicationData.getResource();
    const targetReference = `Communication/${inboundPayload.id}`;

    inboundPayload.subject = { reference: `Patient/${patientId}` };

    const provenance = ProvenanceBuilder.build(
      targetReference,
      actorReference,
      patientId,
      'UPDATE',
      'Zendesk ticket has been updated to this member',
      organizationReference
    );

    const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([inboundPayload], provenance);
    try {
      await fhirProxyClient.send('post', '', fhirBundle);

      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
      communicationsMutate();
    } catch (error: unknown) {
      const errorResponse = ErrorResponse.getMessage(error);
      handleErrorMessage(errorResponse);
    } finally {
      setIsSubmitting(false);
      handleCloseModal();
    }
  };

  const handleCloseModal = () => {
    setIsAssignMemberModalOpen(false);
    setSelectedCallTicket(null);
    setSelectedCallerCard(null);
  };

  const handleDeleteCall = async (communicationResource: Communication) => {
    // Change identifier to deleted
    const helper = new CommunicationHelper(communicationResource);

    helper.setState('deleted');
    helper.setSubject(null);
    //helper.setUknownCallerStatus(true);

    // Now post bundle request
    setIsSubmitting(true);

    try {
      await fhirPut('/Communication/' + helper.getId(), token, helper.getResource());

      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
      communicationsMutate();
      setIsSubmitting(false);
      setDeleteCall(null);
    // Pre-existing swallow, not a new guard: a failed delete leaves the row as it is.
    // eslint-disable-next-line no-empty
    } catch { }
  };

  const handleSelectCallerCard = (patient: Patient) => {
    setSelectedCallerCard(patient);
  };

  const communicationHelper = new CommunicationHelper(debugCommunication);
  const communicationId = communicationHelper.getId();
  const orgId = communicationHelper.getOrgId();
  const memberCommSource =
    communicationHelper.getCommunicationMediumChannelText() === 'phone' ||
      communicationHelper.getCommunicationMediumChannelText() === 'sms'
      ? communicationHelper.getMemberPhoneNumber()
      : communicationHelper.getCommunicationMediumChannelText() === 'email'
        ? communicationHelper.getMemberEmail()
        : 'N/A';
  const hasDeleteCall = Boolean(deleteCall);

  // Extract ticket ID from identifier
  const ticketId =
    debugCommunication.identifier?.find((id) => id.system === 'https://projectwell.io/fhir/identifier/communication/zendesk-ticket-id')
      ?.value || 'N/A';

  // Extract channel from medium
  const channel =
    debugCommunication.medium?.find((med) => med.coding?.[0]?.system === 'https://projectwell.io/fhir/medium/channel')
      ?.text || 'N/A';

  // Extract type from identifier or other source
  const _type =
    debugCommunication.identifier?.find(
      (id) => id.system === 'https://projectwell.io/fhir/identifiers/communication/direction'
    )?.value || 'N/A';

  // Extract assignee - this might need adjustment based on actual data structure
  const assignee = debugCommunication.sender?.display || 'N/A';

  // Extract subject
  const subject = debugCommunication.topic?.text || debugCommunication.subject?.display || 'N/A';

  // Extract description
  const description = debugCommunication.note?.[0]?.text || 'N/A';

  // Extract date time
  const dateTime = debugCommunication.sent || debugCommunication.received || 'N/A';

  // Extract phone/email
  const phone = debugCommunication.extension?.find((ext) =>
    ext.url === 'https://projectwell.io/fhir/extension/communication/zendesk-requester-phone'
  )?.valueString;
  const email = debugCommunication.extension?.find((ext) =>
    ext.url === 'https://projectwell.io/fhir/extension/communication/zendesk-requester-email'
  )?.valueString;

  return (
    <>
      <Table.Row>
        <Table.Cell>{ticketId}</Table.Cell>
        <Table.Cell>{channel}</Table.Cell>
        <Table.Cell>
          {phone || 'N/A'} <br />
          {email || 'N/A'}
        </Table.Cell>
        {/* <Table.Cell>{type}</Table.Cell> */}
        <Table.Cell>{assignee}</Table.Cell>
        <Table.Cell>
          {subject}
          <Popup
            on="click"
            wide
            trigger={
              <Icon
                name="info circle"
                size="small"
                style={{ marginLeft: 6, cursor: 'pointer', verticalAlign: 'middle' }}
              />
            }
            content={
              <div
                style={{
                  maxWidth: 320,
                  maxHeight: 240,
                  overflow: 'auto',
                  padding: 4,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {description}
              </div>
            }
            header="Description"
          />
        </Table.Cell>
        <Table.Cell>{dateTime !== 'N/A' ? moment(dateTime).format('M/D/YY h:mm a') : 'N/A'}</Table.Cell>


        {/* Actions */}
        <Table.Cell>
          <Button color="blue" onClick={() => handleAssignClick(communicationHelper)}>
            Assign
          </Button>
          <>
            <Icon
              name="archive"
              color="red"
              style={{ cursor: 'pointer' }}
              onClick={() => setDeleteCall(debugCommunication)}
            />
            <Confirm
              open={hasDeleteCall}
              content="Are you sure you want to archive this communication?"
              cancelButton="Never mind"
              confirmButton={
                <Button color="blue" loading={isSubmitting}>
                  Yes, archive it
                </Button>
              }
              onCancel={() => setDeleteCall(null)}
              onConfirm={() => handleDeleteCall(deleteCall!)}
            />
          </>
        </Table.Cell>
      </Table.Row>
      {/* Assign Member Modal */}
      {selectedCallTicket?.getId() === communicationId && isAssignMemberModalOpen && (
        <AssignMemberListModal
          handleAssignMember={handleAssignMember}
          handleCloseModal={handleCloseModal}
          handleSelectCallerCard={handleSelectCallerCard}
          isAssignMemberModalOpen={isAssignMemberModalOpen}
          isSubmitting={isSubmitting}
          orgId={orgId!}
          selectedCallTicket={selectedCallTicket}
          selectedCallerCard={selectedCallerCard}
          searchQuery={memberCommSource}
        />
      )}
    </>
  );
};

const _ChannelIcon = (props: { channel?: string; type?: string }) => {
  let iconName: SemanticICONS = 'phone volume';
  let iconColor: SemanticCOLORS = 'green';

  switch (props.channel) {
    case 'voice':
      iconName = 'phone volume';
      if (props.type === 'outbound') {
        iconColor = 'orange';
      } else if (props.type === 'voicemail') {
        iconColor = 'purple';
      }
      break;
    case 'email':
      iconName = 'mail outline';
      iconColor = 'grey';
      break;
    case 'sms':
      iconName = 'chat';
      iconColor = 'blue';
      break;
    default:
      break;
  }

  return <Icon color={iconColor} name={iconName} size="large" />;
};

export default ZendeskRow;
